/** Minimal date range shape shared by PlanInstance ownership helpers. */
export interface DatedPlanInstance {
  id: string;
  startDate: string;
  endDate: string;
}

/**
 * Pick one deterministic instance for a concrete date.
 *
 * Weekly rows should be Monday-aligned, but older data can contain partial or
 * overlapping rows. Prefer the canonical Monday row, then a row covering the
 * complete week, then the narrowest/latest remaining row. Never depend on API
 * response order for a write target.
 */
export function selectPlanInstanceForDate<T extends DatedPlanInstance>(instances: readonly T[], dateKey: string): T | undefined {
  const candidates = instances.filter(instance => instance.startDate <= dateKey && instance.endDate >= dateKey);
  if (candidates.length === 0) return undefined;

  const weekStart = isoWeekStart(dateKey);
  return selectPreferredInstance(candidates, weekStart, addDays(weekStart, 6), false);
}

/**
 * Pick the instance that owns a requested Monday-start week.
 *
 * A partial row beginning later in the week is deliberately not a match. The
 * caller may create the missing canonical row; range commands can still target
 * the partial row separately when cleaning legacy data.
 */
export function selectPlanInstanceForWeek<T extends DatedPlanInstance>(
  instances: readonly T[],
  weekStart: string,
  weekEnd: string
): T | undefined {
  const candidates = instances.filter(instance => instance.startDate <= weekStart && instance.endDate >= weekStart);
  if (candidates.length === 0) return undefined;
  return selectPreferredInstance(candidates, weekStart, weekEnd, true);
}

/** Return every instance touching a visible date range in stable order. */
export function filterPlanInstancesOverlappingRange<T extends DatedPlanInstance>(
  instances: readonly T[],
  rangeStart: string,
  rangeEnd: string
): T[] {
  return instances
    .filter(instance => instance.endDate >= rangeStart && instance.startDate <= rangeEnd)
    .sort(
      (left, right) =>
        left.startDate.localeCompare(right.startDate) || left.endDate.localeCompare(right.endDate) || left.id.localeCompare(right.id)
    );
}

function selectPreferredInstance<T extends DatedPlanInstance>(
  candidates: readonly T[],
  weekStart: string,
  weekEnd: string,
  preferFullWeek: boolean
): T {
  return [...candidates].sort((left, right) => {
    const leftIsCanonical = left.startDate === weekStart;
    const rightIsCanonical = right.startDate === weekStart;
    if (leftIsCanonical !== rightIsCanonical) return leftIsCanonical ? -1 : 1;

    if (preferFullWeek) {
      const leftCoversWeek = left.startDate <= weekStart && left.endDate >= weekEnd;
      const rightCoversWeek = right.startDate <= weekStart && right.endDate >= weekEnd;
      if (leftCoversWeek !== rightCoversWeek) return leftCoversWeek ? -1 : 1;
    }

    const spanDifference = dateSpan(left) - dateSpan(right);
    if (spanDifference !== 0) return spanDifference;

    const latestStart = right.startDate.localeCompare(left.startDate);
    if (latestStart !== 0) return latestStart;

    const earliestEnd = left.endDate.localeCompare(right.endDate);
    if (earliestEnd !== 0) return earliestEnd;

    return left.id.localeCompare(right.id);
  })[0];
}

function isoWeekStart(dateKey: string): string {
  const date = new Date(`${dateKey}T00:00:00Z`);
  const day = date.getUTCDay();
  date.setUTCDate(date.getUTCDate() + (day === 0 ? -6 : 1 - day));
  return date.toISOString().slice(0, 10);
}

function addDays(dateKey: string, days: number): string {
  const date = new Date(`${dateKey}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function dateSpan(instance: DatedPlanInstance): number {
  const start = Date.parse(`${instance.startDate}T00:00:00Z`);
  const end = Date.parse(`${instance.endDate}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return Number.MAX_SAFE_INTEGER;
  return end - start;
}
